import React, { Fragment, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { defaultPaginationProps } from '@/shared/helpers';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import LoadingIcon from 'mdi-react/LoadingIcon';
import api from '@/utils/apiClient';
import PencilIcon from 'mdi-react/PencilIcon';
import { Button, ButtonToolbar, Modal } from 'reactstrap';
import Select from 'react-select';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import EyeIcon from 'mdi-react/EyeIcon';
import ViewMoreDialog from './components/ViewMoreDialog';

const columns = [
  {
    id: 'picUrl',
    disablePadding: true,
    label: 'Picture',
  },
  {
    id: 'orgName',
    disablePadding: true,
    label: 'Organization Name',
  },
  {
    id: 'orgEmail',
    disablePadding: true,
    label: 'Email',
  },
  {
    id: 'learningPathList',
    disablePadding: true,
    label: 'Learning Paths',
  },
  {
    id: 'action',
    disablePadding: true,
    label: 'Actions',
  },
];

const AssignedLPTableColumns = ({ order, orderBy, onRequestSort }) => {
  const createSortHandler = (property, onRequestSort) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <Fragment>
      <TableRow>
        {columns.map((row, idx) => (
          <TableCell
            className="material-table__cell material-table__cell--sort material-table__cell-center"
            key={row.id}
            align={'center'}
            padding={row.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === row.id ? order : false}
          >
            {idx === 0 ? (
              <b>{row.label}</b>
            ) : (
              <TableSortLabel
                active={orderBy === row.id}
                direction={order}
                onClick={createSortHandler(row.id, onRequestSort)}
                className="material-table__sort-label"
                dir="ltr"
              >
                <b>{row.label}</b>
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </Fragment>
  );
};

const UnassignDialog = ({ show, onHide, tenantId, learningPaths }) => {
  const { rtl } = useSelector((state) => state, shallowEqual);

  const initialValues = {
    isOpen: show,
    title: 'Unassignment Confirmation',
    message: 'Select learning path to unassign',
    hide: onHide,
    loading: false,
    unassignmentSuccess: false,
    unassignmentError: false,
  };
  const [modal, setModal] = React.useState(initialValues);
  const [selectedLP, setSelectedLP] = React.useState({
    value: -1,
    label: 'Select Learning Path',
  });

  React.useEffect(() => {
    setModal({ ...modal, isOpen: show, hide: onHide });
  }, [show, onHide]);

  const performUnassignment = async () => {
    setModal({
      ...modal,
      loading: true,
      title: 'Performing Unassignment',
      message: 'Unassignment in progress...',
    });

    /*Make api call for performing assignment*/
    let response = await api.learningPaths.unAssignLPToOrganization({
      learningPathId: selectedLP.value,
      tenantIds: [tenantId],
    });

    /*update modal properties based on response*/
    let updatedModal = {};
    if (response !== null) {
      if (response.isSuccess) {
        updatedModal = {
          loading: false,
          unassignmentSuccess: true,
          title: 'Done',
          message: 'Unassignment completed successfully!',
        };
      } else {
        updatedModal = {
          loading: false,
          unassignmentError: true,
          title: 'Opps',
          message: 'Error performing unassignment!',
        };
      }
    }
    setModal({ ...modal, ...updatedModal });
  };

  const resetModal = () => {
    setModal(initialValues);
    setSelectedLP({ value: -1, label: 'Select Learning Path' });
  };

  return (
    <Modal
      isOpen={show}
      toggle={modal.hide}
      keyboard={false}
      onClosed={resetModal}
      backdrop={'static'}
      modalClassName={`${rtl.direction}-support`}
      className="modal-dialog--primary modal-dialog"
    >
      <div className="modal__header">
        <button
          className="lnr lnr-cross modal__close-btn"
          aria-label="modal__close-btn"
          type="button"
          onClick={modal.hide}
        />
        {!(modal.loading || modal.unassignmentSuccess || modal.unassignmentError) && (
          <span className="lnr lnr-unlink modal__title-icon" />
        )}
        {modal.unassignmentSuccess && (
          <span className="lnr lnr-thumbs-up modal__title-icon success" />
        )}
        {modal.unassignmentError && (
          <span className="lnr lnr-cross-circle modal__title-icon error" />
        )}
        <h4 className="text-modal  modal__title">{modal.title}</h4>
        <div className="modal__body">
          {modal.loading && (
            <div className="panel__refresh position-relative" style={{ height: 75 }}>
              <LoadingIcon />
            </div>
          )}
          {modal.message}
          {!(modal.unassignmentSuccess || modal.unassignmentError || modal.loading) && (
            <Select
              name="selectLearningPath"
              value={selectedLP}
              onChange={setSelectedLP}
              autosize={true}
              options={learningPaths.map((lp) => ({ value: lp.lpId, label: lp.fullName }))}
              clearable={false}
              className="react-select mt-3 mb-3"
              placeholder={'Select Learning Path'}
              classNamePrefix="react-select"
            />
          )}
        </div>
        <ButtonToolbar className="modal__footer">
          {modal.unassignmentError && (
            <Button className="modal_cancel" onClick={onHide} outline>
              Close
            </Button>
          )}
          {modal.unassignmentSuccess && (
            <>
              <Button className="modal_cancel" onClick={onHide} outline>
                Okay
              </Button>
            </>
          )}
          {!(modal.unassignmentSuccess || modal.unassignmentError || modal.loading) && (
            <>
              <Button className="modal_cancel" onClick={onHide}>
                Cancel
              </Button>
              <Button className="modal_ok" outline color="danger" onClick={performUnassignment}>
                Yes, Unassign
              </Button>
            </>
          )}
        </ButtonToolbar>
      </div>
    </Modal>
  );
};

const OrganizationAssignedLPTable = () => {
  const [filters, setFilters] = React.useState({
    pageNo: 0,
    pageSize: 10,
    sortBy: '',
    orgName: '',
    isSortOrderDescending: false,
  });
  const { pageSize: rowsPerPage, pageNo: page, isSortOrderDescending, sortBy } = filters;
  const [lpOrganizationData, setLPOrganizationData] = React.useState({
    totalRecords: 0,
    loading: false,
    error: '',
    records: [],
  });
  const { totalRecords, loading, error, records } = lpOrganizationData;
  const colLength = columns.length;
  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - records.length : 0;
  const [unAssignDialog, setUnAssignDialog] = React.useState({
    isOpen: false,
    tenantId: null,
    learningPaths: [],
  });
  const [viewMoreDialog, setViewMoreDialog] = React.useState({ isOpen: false, learningPaths: [] });

  const [isSearched, setIsSearched] = useState(false);
  const searchRef = useRef(null);

  const handleSearch = () => {
    setIsSearched(true);
    setFilters({ ...filters, orgName: searchRef.current.value });
  };

  const handleClear = () => {
    if (searchRef?.current?.value) searchRef.current.value = '';
    setIsSearched(false);
    setFilters({ ...filters, orgName: '' });
  };

  React.useEffect(() => {
    if (unAssignDialog.isOpen === false) getLPData();
  }, [unAssignDialog.isOpen, filters]);

  const getLPData = async () => {
    const defaultError = 'Error occurred getting organization assigned learning path list';
    try {
      setLPOrganizationData({ ...lpOrganizationData, loading: true });
      const { data: res, isSuccess } = await api.learningPaths.getAssignedOrganizationLP({
        ...filters,
      });
      if (isSuccess) {
        setLPOrganizationData({
          ...lpOrganizationData,
          loading: false,
          error: '',
          records: res.data,
          totalRecords: res?.totalElements,
        });
      } else throw new Error(res?.messages?.[0] ?? defaultError);
    } catch (error) {
      setLPOrganizationData({
        ...lpOrganizationData,
        loading: false,
        error: error.msg,
        totalRecords: 0,
        records: [],
      });
      console.error(defaultError, error.message);
    }
  };

  const handleRequestSort = (e, sortBy) => {
    setFilters({
      ...filters,
      sortBy,
      isSortOrderDescending: !filters.isSortOrderDescending,
    });
  };

  const handleChangePage = (event, currentPage) => {
    setFilters({ ...filters, pageNo: currentPage });
  };

  const handleChangeRowsPerPage = (e) => {
    setFilters({ ...filters, pageNo: 0, pageSize: e.target.value });
  };

  const renderTableStatus = () =>
    (loading || !!error || totalRecords === 0) && (
      <TableRow>
        <TableCell colSpan={colLength}>
          {loading && (
            <div className={'text-center overflow-hidden'}>
              <div className="panel__refresh position-relative" style={{ height: 75 }}>
                <LoadingIcon />
              </div>
            </div>
          )}
          {totalRecords === 0 && !error && !loading && (
            <div className={'text-center'}>
              <p className={'m-5'}>No records found with applied filter.</p>
            </div>
          )}
          {(!!error || loading) && (
            <div className={'text-center'}>
              <p className={!!error ? 'm-5 error' : ''}>{error || 'Loading...'}</p>
            </div>
          )}
        </TableCell>
      </TableRow>
    );

  return (
    <>
      <div className="table__search">
        <div className="table__search_item">
          <b>Organization Name</b>
          <div className="form">
            <div className="form__form-group">
              <div className="form__form-group-field">
                <div className="form__form-group-icon" style={{ height: 37 }}>
                  <MagnifyIcon />
                </div>
                <input
                  ref={searchRef}
                  className="search-bar-search-text"
                  name="searchText"
                  type="text"
                  placeholder="Search by Organization Name"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="table__search_item mt-3">
          <Button color="primary" onClick={handleSearch}>
            {' '}
            Search{' '}
          </Button>
        </div>
        <div className="table__search_item mt-3">
          <Button color="secondary" onClick={handleClear} outline>
            Clear
          </Button>
        </div>
      </div>
      <div className="material-table__wrap">
        <Table className="material-table">
          <TableBody>
            <AssignedLPTableColumns
              order={isSortOrderDescending ? 'desc' : 'asc'}
              orderBy={sortBy}
              onRequestSort={handleRequestSort}
              rowCount={rowsPerPage}
            />

            {renderTableStatus()}
            {records.map((data) => (
              <TableRow key={data.tenantId}>
                <TableCell align="center" className="material-table__cell">
                  <img className="program_logo_img" src={data?.picUrl} alt="Picture" />
                </TableCell>
                <TableCell align="center" className="material-table__cell">
                  {data?.orgName}{' '}
                </TableCell>
                <TableCell align="center" className="material-table__cell">
                  {data?.orgEmail}{' '}
                </TableCell>
                <TableCell align="center" className="material-table__cell">
                  {data?.learningPathList?.map((m) => m.fullName).join(', ')}
                </TableCell>
                <TableCell
                  align="center"
                  className="material-table__cell"
                  style={{ minWidth: 260 }}
                >
                  <Button
                    className="icon mb-0"
                    color="primary"
                    outline
                    onClick={() =>
                      setUnAssignDialog({
                        isOpen: true,
                        tenantId: data.tenantId,
                        learningPaths: data?.learningPathList,
                      })
                    }
                  >
                    <p>
                      <PencilIcon /> UnAssign
                    </p>
                  </Button>
                  <Button
                    className="icon mb-0"
                    color="primary"
                    outline
                    onClick={() =>
                      setViewMoreDialog({
                        isOpen: true,
                        learningPaths: data?.learningPathList ?? [],
                      })
                    }
                  >
                    <p>
                      <EyeIcon /> View More
                    </p>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            <UnassignDialog
              onHide={() => {
                setUnAssignDialog({ ...unAssignDialog, isOpen: false });
                getLPData();
              }}
              show={unAssignDialog.isOpen}
              tenantId={unAssignDialog.tenantId}
              learningPaths={unAssignDialog.learningPaths}
            />
            <ViewMoreDialog
              onHide={() => setViewMoreDialog({ ...viewMoreDialog, isOpen: false })}
              show={viewMoreDialog.isOpen}
              learningPaths={viewMoreDialog.learningPaths}
            />
            {/*Rendering empty rows for better UI*/}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={colLength} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </>
  );
};

export default OrganizationAssignedLPTable;
